<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import { useSocketStore } from '@/stores/socketStore';
import activityFeedService from '@/services/activityFeed';
import type { ActivityFeedEntry, ActivityEntityType } from '@/types/activityFeed';
import { formatDistanceToNow } from 'date-fns';
import { useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import ScrollPanel from 'primevue/scrollpanel';
import ProgressSpinner from 'primevue/progressspinner';
import Avatar from 'primevue/avatar';

const router = useRouter();
const toast = useToast();
const socketStore = useSocketStore();

// Props
interface Props {
    maxHeight?: string;
    limit?: number;
    entityId?: string;
    entityType?: ActivityEntityType;
    entityTypes?: ActivityEntityType[];
}

const props = withDefaults(defineProps<Props>(), {
    maxHeight: '600px',
    limit: 20
});

// State
const activities = ref<ActivityFeedEntry[]>([]);
const loading = ref(false);
const loadingMore = ref(false);
const offset = ref(0);
const hasMore = ref(true);

// Get user initials for avatar
const getUserInitials = (activity: ActivityFeedEntry): string => {
    if (activity.first_name && activity.last_name) {
        return `${activity.first_name[0]}${activity.last_name[0]}`.toUpperCase();
    }
    return 'SYS';
};

// Get avatar color based on user
const getAvatarColor = (activity: ActivityFeedEntry): string => {
    if (!activity.user_id) return '#6366f1'; // System events - indigo
    
    // Generate consistent color based on user ID
    const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];
    return colors[activity.user_id % colors.length];
};

// Format activity message with rich content
const formatActivityMessage = (activity: ActivityFeedEntry): string => {
    const userName = activity.first_name && activity.last_name 
        ? `${activity.first_name} ${activity.last_name}` 
        : 'System';

    switch (activity.event_type) {
        case 'unit_checked':
            if (activity.metadata?.temperature_delta && Math.abs(activity.metadata.temperature_delta) > 5) {
                return `${userName} checked unit <strong>${activity.entity_id}</strong> ⚠️ Temperature delta: ${activity.metadata.temperature_delta}°C`;
            }
            if (activity.metadata?.alarms && activity.metadata.alarms.length > 0) {
                return `${userName} checked unit <strong>${activity.entity_id}</strong> 🚨 ${activity.metadata.alarms.length} alarm(s) active`;
            }
            return `${userName} checked unit <strong>${activity.entity_id}</strong> ✅ All systems normal`;

        case 'train_arrival':
            return `🚂 Train <strong>${activity.entity_id}</strong> has arrived at the yard`;

        case 'train_departure':
            return `🚂 Train <strong>${activity.entity_id}</strong> has departed`;

        case 'request_completed':
            const outcome = activity.metadata?.outcome === 'successful' ? '✅' : '❌';
            return `${userName} completed request <strong>#${activity.entity_id}</strong> ${outcome}`;

        case 'request_created':
            return `${userName} created a new request <strong>#${activity.entity_id}</strong>`;

        case 'clip_installed':
            return `${userName} installed clip-on <strong>${activity.entity_id}</strong> on unit ${activity.metadata?.unit_number || 'unknown'}`;

        case 'clip_removed':
            return `${userName} removed clip-on <strong>${activity.entity_id}</strong>`;

        case 'clip_pretripped':
            return `${userName} pretripped clip-on <strong>${activity.entity_id}</strong> ✅`;

        case 'fuel_truck_refueled':
            return `⛽ ${userName} refueled truck <strong>#${activity.entity_id}</strong> - ${activity.metadata?.litres || 0}L added`;

        case 'equipment_refueled':
            return `⛽ ${userName} refueled <strong>${activity.entity_id}</strong> - ${activity.metadata?.litres || 0}L added`;

        case 'yard_check_started':
            return `${userName} started a yard check 📋`;

        case 'yard_check_completed':
            return `${userName} completed yard check with ${activity.metadata?.units_checked || 0} units checked`;

        case 'time_clock_in':
            return `${userName} clocked in 🕐`;

        case 'time_clock_out':
            return `${userName} clocked out 🕐`;

        default:
            return activity.title;
    }
};

// Get activity icon
const getActivityIcon = (activity: ActivityFeedEntry): string => {
    const iconMap: Record<string, string> = {
        'unit_checked': 'pi-search',
        'train_arrival': 'pi-sign-in',
        'train_departure': 'pi-sign-out',
        'request_created': 'pi-plus-circle',
        'request_completed': 'pi-check-circle',
        'clip_installed': 'pi-plus',
        'clip_removed': 'pi-minus',
        'fuel_truck_refueled': 'pi-truck',
        'yard_check_started': 'pi-clipboard',
        'time_clock_in': 'pi-clock',
        'time_clock_out': 'pi-clock'
    };
    
    return iconMap[activity.event_type] || 'pi-info-circle';
};

// Get activity accent color
const getActivityAccent = (activity: ActivityFeedEntry): string => {
    if (activity.severity === 'error') return '#ef4444';
    if (activity.severity === 'warning') return '#f59e0b';
    if (activity.severity === 'success') return '#10b981';
    
    // Type-based colors
    const typeColors: Record<string, string> = {
        'train_arrival': '#3b82f6',
        'train_departure': '#3b82f6',
        'request_completed': '#10b981',
        'unit_checked': activity.metadata?.temperature_delta > 5 ? '#f59e0b' : '#10b981',
        'fuel_truck_refueled': '#14b8a6',
        'clip_installed': '#8b5cf6'
    };
    
    return typeColors[activity.event_type] || '#6b7280';
};

// Load activities
const loadActivities = async (append = false) => {
    if (!append) {
        loading.value = true;
        offset.value = 0;
    } else {
        loadingMore.value = true;
    }

    try {
        const response = await activityFeedService.getActivityFeed({
            limit: props.limit,
            offset: offset.value,
            entity_id: props.entityId,
            entity_types: props.entityTypes || (props.entityType ? [props.entityType] : undefined)
        });

        if (append) {
            activities.value = [...activities.value, ...response.data];
        } else {
            activities.value = response.data;
        }

        hasMore.value = response.data.length === props.limit;
        offset.value += response.data.length;

    } catch (error) {
        console.error('Error loading activities:', error);
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load activity feed',
            life: 3000
        });
    } finally {
        loading.value = false;
        loadingMore.value = false;
    }
};

// Handle scroll to load more
const handleScroll = (event: any) => {
    const element = event.target;
    const scrollPercentage = (element.scrollTop + element.clientHeight) / element.scrollHeight;
    
    if (scrollPercentage > 0.8 && hasMore.value && !loadingMore.value) {
        loadActivities(true);
    }
};

// Handle new activity from socket
const handleNewActivity = (activity: ActivityFeedEntry) => {
    // Add to beginning with animation
    activities.value.unshift(activity);
    
    // Subtle notification for important events
    if (activity.severity === 'error' || activity.severity === 'warning') {
        toast.add({
            severity: activity.severity,
            summary: 'Alert',
            detail: activity.title,
            life: 3000
        });
    }
};

// Navigate to entity
const navigateToEntity = (activity: ActivityFeedEntry) => {
    switch (activity.entity_type) {
        case 'train':
            router.push(`/trains/${activity.entity_id}`);
            break;
        case 'request':
            router.push(`/requests/${activity.entity_id}`);
            break;
        case 'unit':
            router.push(`/units/${activity.entity_id}`);
            break;
        case 'yard_check':
            router.push(`/yard-check/${activity.entity_id}`);
            break;
        case 'clip_on':
            router.push(`/clip-ons/${activity.entity_id}`);
            break;
        default:
            // No navigation for some types
            break;
    }
};

// Lifecycle
onMounted(() => {
    loadActivities();
    
    // Listen for new activities via socket
    if (socketStore.socket) {
        socketStore.socket.on('activity_feed:new', handleNewActivity);
    }
});

onUnmounted(() => {
    // Clean up socket listeners
    if (socketStore.socket) {
        socketStore.socket.off('activity_feed:new', handleNewActivity);
    }
});
</script>

<template>
    <div class="activity-feed-social">
        <div 
            class="feed-container"
            :style="{ height: maxHeight, overflowY: 'auto' }"
            @scroll="handleScroll"
        >
            <!-- Loading spinner -->
            <div v-if="loading" class="text-center p-4">
                <ProgressSpinner />
            </div>
            
            <!-- Empty state -->
            <div v-else-if="activities.length === 0" class="empty-state">
                <i class="pi pi-comments text-4xl mb-3"></i>
                <p class="text-lg">No activity yet</p>
                <p class="text-sm text-500">Activities will appear here as they happen</p>
            </div>
            
            <!-- Activity items -->
            <TransitionGroup v-else name="feed" tag="div">
                <div 
                    v-for="activity in activities" 
                    :key="activity.id"
                    class="activity-item"
                    @click="navigateToEntity(activity)"
                >
                    <!-- Left side - Avatar -->
                    <div class="activity-avatar">
                        <Avatar 
                            :label="getUserInitials(activity)"
                            :style="{ 
                                backgroundColor: getAvatarColor(activity),
                                color: 'white'
                            }"
                            shape="circle"
                            size="large"
                        />
                    </div>
                    
                    <!-- Right side - Content -->
                    <div class="activity-content">
                        <!-- Activity message -->
                        <div 
                            class="activity-message"
                            v-html="formatActivityMessage(activity)"
                        ></div>
                        
                        <!-- Metadata badges -->
                        <div v-if="activity.metadata" class="activity-meta">
                            <span 
                                v-if="activity.metadata.temperature_delta"
                                class="meta-badge temp-delta"
                            >
                                🌡️ Δ{{ activity.metadata.temperature_delta }}°C
                            </span>
                            <span 
                                v-if="activity.metadata.alarms?.length > 0"
                                class="meta-badge alarms"
                            >
                                🚨 {{ activity.metadata.alarms.length }} alarm(s)
                            </span>
                            <span 
                                v-if="activity.metadata.litres"
                                class="meta-badge fuel"
                            >
                                ⛽ {{ activity.metadata.litres }}L
                            </span>
                        </div>
                        
                        <!-- Timestamp -->
                        <div class="activity-time">
                            <i class="pi pi-clock text-xs mr-1"></i>
                            {{ formatDistanceToNow(new Date(activity.created_at), { addSuffix: true }) }}
                        </div>
                    </div>
                    
                    <!-- Accent indicator -->
                    <div 
                        class="activity-accent"
                        :style="{ backgroundColor: getActivityAccent(activity) }"
                    ></div>
                </div>
            </TransitionGroup>
            
            <!-- Load more indicator -->
            <div v-if="loadingMore" class="text-center p-3">
                <ProgressSpinner 
                    style="width: 30px; height: 30px" 
                    strokeWidth="4"
                />
            </div>
            
            <!-- End of feed -->
            <div v-if="!hasMore && activities.length > 0" class="end-of-feed">
                <p class="text-sm text-500">You're all caught up!</p>
            </div>
        </div>
    </div>
</template>

<style scoped>
.activity-feed-social {
    height: 100%;
}

.feed-container {
    overflow-y: auto;
    scroll-behavior: smooth;
}

.feed-container::-webkit-scrollbar {
    width: 6px;
}

.feed-container::-webkit-scrollbar-track {
    background: var(--surface-100);
}

.feed-container::-webkit-scrollbar-thumb {
    background: var(--surface-400);
    border-radius: 3px;
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    color: var(--text-color-secondary);
}

.activity-item {
    display: flex;
    gap: 1rem;
    padding: 1rem;
    border-bottom: 1px solid var(--surface-200);
    position: relative;
    cursor: pointer;
    transition: all 0.2s ease;
}

.activity-item:hover {
    background-color: var(--surface-50);
}

.activity-avatar {
    flex-shrink: 0;
}

.activity-content {
    flex: 1;
    min-width: 0;
}

.activity-message {
    color: var(--text-color);
    line-height: 1.5;
    margin-bottom: 0.5rem;
    word-wrap: break-word;
}

.activity-message :deep(strong) {
    font-weight: 600;
    color: var(--primary-color);
}

.activity-meta {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
    flex-wrap: wrap;
}

.meta-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.5rem;
    background-color: var(--surface-100);
    border-radius: 1rem;
    font-size: 0.75rem;
    font-weight: 500;
}

.meta-badge.temp-delta {
    background-color: #fef3c7;
    color: #92400e;
}

.meta-badge.alarms {
    background-color: #fee2e2;
    color: #991b1b;
}

.meta-badge.fuel {
    background-color: #d1fae5;
    color: #065f46;
}

.activity-time {
    font-size: 0.75rem;
    color: var(--text-color-secondary);
    display: flex;
    align-items: center;
}

.activity-accent {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
}

.end-of-feed {
    text-align: center;
    padding: 2rem;
}

/* Animations */
.feed-enter-active {
    transition: all 0.3s ease-out;
}

.feed-enter-from {
    transform: translateY(-20px);
    opacity: 0;
}

.feed-leave-active {
    transition: all 0.3s ease-in;
}

.feed-leave-to {
    transform: translateX(20px);
    opacity: 0;
}

/* Dark mode adjustments */
:root[class*='dark'] {
    .activity-item:hover {
        background-color: var(--surface-800);
    }
    
    .meta-badge {
        background-color: var(--surface-800);
    }
    
    .feed-container::-webkit-scrollbar-track {
        background: var(--surface-800);
    }
    
    .feed-container::-webkit-scrollbar-thumb {
        background: var(--surface-600);
    }
}
</style>